/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingDown, Wallet, CreditCard, AlertCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useCredits } from '../../hooks/useCredits';
import axiosClient from '../../api/axiosClient';
import UnsubscribeModal from '../../components/modals/UnsubscribeModal';

const ACCENT = '#7d6633';

// Dashboard-style helpers (kept local for now to avoid refactors)
const Card = ({ children, className = '', style }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      className
    }
    style={style}
  >
    {children}
  </div>
);

const CardHeader = ({ title, subtitle, icon, action }) => (
  <div className="flex items-center justify-between p-2.5 md:p-3 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-lg bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle ? <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p> : null}
      </div>
    </div>
    {action}
  </div>
);

const Stat = ({ label, value, icon, tone = 'default' }) => {
  const toneClass =
    tone === 'danger'
      ? 'text-rose-600 dark:text-rose-400'
      : tone === 'accent'
      ? 'text-[#7d6633] dark:text-[#c9a038]'
      : 'text-zinc-900 dark:text-zinc-100';

  return (
    <motion.div whileHover={{ y: -2, scale: 1.01 }} transition={{ duration: 0.2 }} className="group">
      <Card className="p-3 md:p-4 border-l-4" style={{ borderLeftColor: ACCENT }}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-2">{label}</p>
            <div className="flex items-end gap-2">
              <span className={`text-2xl md:text-3xl font-bold ${toneClass}`}>{value}</span>
            </div>
          </div>
          <div
            className="h-11 w-11 rounded-xl border-2 border-white/10 dark:border-white/10 flex items-center justify-center group-hover:scale-105 group-hover:rotate-3 transition-all duration-300"
            style={{ background: `${ACCENT}25` }}
          >
            {icon}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

const Credits = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const { credits, loading: creditsLoading, refetch: refetchCredits } = useCredits();

  const [creditPacks, setCreditPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(null);

  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);

  const creditsRemaining = credits?.credits_remaining ?? 0;
  const creditsUsed = credits?.credits_used ?? 0;
  const creditsGranted = credits?.credits_total_granted ?? 0;

  // Fetch packs
  useEffect(() => {
    let cancelled = false;
    async function fetchCreditPacks() {
      try {
        setPacksLoading(true);
        const response = await axiosClient.get('/credits/packs');
        if (!cancelled && response.data?.ok) {
          setCreditPacks(Array.isArray(response.data.packs) ? response.data.packs : []);
        }
      } catch (err) {
        console.error('Error fetching credit packs:', err);
        if (!cancelled) setError('Failed to load credit packs.');
      } finally {
        if (!cancelled) setPacksLoading(false);
      }
    }
    fetchCreditPacks();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch subscription status
  useEffect(() => {
    let cancelled = false;
    async function fetchSubscriptionStatus() {
      try {
        setSubscriptionLoading(true);
        const response = await axiosClient.get('/subscriptions/status');
        if (!cancelled && response.data?.ok) {
          setSubscriptionStatus(response.data);
        }
      } catch (err) {
        console.error('Error fetching subscription status:', err);
      } finally {
        if (!cancelled) setSubscriptionLoading(false);
      }
    }
    fetchSubscriptionStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle Stripe return params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    const legacyFailed = urlParams.get('success') === 'false';

    if (success === 'true') {
      setSuccessMessage('Credits purchased successfully. Your balance will update shortly.');
      refetchCredits();
      window.history.replaceState({}, '', '/admin/credits');
      setTimeout(() => setSuccessMessage(''), 5000);
    } else if (canceled === 'true' || legacyFailed) {
      setError('Payment cancelled. No credits were added.');
      window.history.replaceState({}, '', '/admin/credits');
      setTimeout(() => setError(''), 5000);
    }
  }, [refetchCredits]);

  const handlePurchaseCredits = async (packId) => {
    try {
      setPurchaseLoading(packId);
      setError('');

      const response = await axiosClient.post('/credits/checkout-session', { packId });
      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
        return;
      }
      throw new Error('No checkout URL received');
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
      setPurchaseLoading(null);
    }
  };

  const handleUnsubscribe = async ({ reason, feedback }) => {
    try {
      setUnsubscribeLoading(true);
      setError('');

      const response = await axiosClient.post('/subscriptions/unsubscribe', {
        reason,
        feedback: feedback || undefined
      });

      if (response.data?.ok) {
        setSuccessMessage('Subscription cancelled successfully. We\'re sorry to see you go.');
        setShowUnsubscribeModal(false);
        
        // Refresh subscription status
        const statusResponse = await axiosClient.get('/subscriptions/status');
        if (statusResponse.data?.ok) {
          setSubscriptionStatus(statusResponse.data);
        }

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Error unsubscribing:', err);
      setError(err.response?.data?.error || 'Failed to cancel subscription. Please try again.');
      setShowUnsubscribeModal(false);
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Top header (dashboard-like) */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-100">{t('admin.credits_billing_title')}</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
            {t('admin.credits_billing_subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetchCredits()}
            className="px-4 py-2.5 rounded-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-white/80 dark:hover:bg-zinc-900/80 transition-all focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30"
            style={{ '--accent': ACCENT }}
            type="button"
          >
            {t('admin.credits_refresh_balance')}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {(successMessage || error) && (
        <Card className="mb-4 md:mb-6 overflow-hidden">
          <div
            className={`px-4 py-3 text-sm font-medium ${
              successMessage
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200'
            }`}
          >
            {successMessage || error}
          </div>
        </Card>
      )}

      {/* Stats row (dashboard-style) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
        <Stat
          label="Credits Available"
          value={creditsLoading ? '...' : String(creditsRemaining)}
          // Match the admin topbar: credits number uses normal admin text color (not gold/yellow).
          tone={creditsRemaining === 0 ? 'danger' : 'default'}
          icon={<Wallet className="h-5 w-5" style={{ color: ACCENT }} />}
        />
        <Stat
          label="Credits Used"
          value={creditsLoading ? '...' : String(creditsUsed)}
          icon={<TrendingDown className="h-5 w-5" style={{ color: ACCENT }} />}
        />
        <Stat
          label="Total Granted"
          value={creditsLoading ? '...' : String(creditsGranted)}
          icon={<Sparkles className="h-5 w-5" style={{ color: ACCENT }} />}
        />
      </div>

      {/* Subscription Status Card */}
      {!subscriptionLoading && subscriptionStatus?.hasSubscription && (
        <Card className="mb-4 md:mb-6">
          <CardHeader
            title="Subscription"
            subtitle="Manage your Hairchitech subscription"
            icon={<CreditCard className="h-4 w-4" style={{ color: ACCENT }} />}
          />
          <div className="p-3 md:p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    subscriptionStatus.status === 'active' 
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                      : subscriptionStatus.status === 'cancelled'
                      ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'
                  }`}>
                    {subscriptionStatus.status === 'active' ? 'ACTIVE' : subscriptionStatus.status.toUpperCase()}
                  </div>
                  {subscriptionStatus.plan && (
                    <span className={`text-sm font-semibold ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                      {subscriptionStatus.plan.charAt(0).toUpperCase() + subscriptionStatus.plan.slice(1)} Plan
                    </span>
                  )}
                </div>
                
                {subscriptionStatus.cancelAtPeriodEnd && subscriptionStatus.currentPeriodEnd && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50">
                    <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Subscription will end on {new Date(subscriptionStatus.currentPeriodEnd).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                        You can still use all features until then.
                      </p>
                    </div>
                  </div>
                )}

                {subscriptionStatus.status === 'active' && !subscriptionStatus.cancelAtPeriodEnd && subscriptionStatus.currentPeriodEnd && (
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    Renews on {new Date(subscriptionStatus.currentPeriodEnd).toLocaleDateString()}
                  </p>
                )}

                {subscriptionStatus.status === 'cancelled' && (
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    {t('admin.credits_subscription_cancelled')} 
                    <a href="/pricing" className="ml-1 font-semibold" style={{ color: ACCENT }}>
                      {t('admin.credits_reactivate')}
                    </a>
                  </p>
                )}
              </div>

              {subscriptionStatus.status === 'active' && !subscriptionStatus.cancelAtPeriodEnd && (
                <button
                  onClick={() => setShowUnsubscribeModal(true)}
                  className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all border-2 ${
                    isDark
                      ? 'border-red-800 text-red-400 hover:bg-red-900/20'
                      : 'border-red-200 text-red-700 hover:bg-red-50'
                  }`}
                >
                  {t('admin.credits_cancel_subscription')}
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Packs (dashboard-like section card) */}
      <Card className="mb-4 md:mb-6">
        <CardHeader
          title="Top up credits"
          subtitle="One-time purchase. Secure checkout via Stripe."
          icon={<img src="/images/icons/coin_icon.png" alt="Credits" className="h-5 w-5" />}
        />
        <div className="p-3 md:p-4">
          {packsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-white/20 dark:border-white/10 bg-white/40 dark:bg-zinc-900/40 backdrop-blur-xl p-4 animate-pulse">
                  <div className="h-4 w-24 rounded bg-zinc-200/70 dark:bg-zinc-800/70 mb-3" />
                  <div className="h-8 w-32 rounded bg-zinc-200/70 dark:bg-zinc-800/70 mb-4" />
                  <div className="h-10 w-full rounded bg-zinc-200/70 dark:bg-zinc-800/70" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {creditPacks.map((pack) => {
                const priceDollars = (pack.price / 100).toFixed(2);
                const perCredit = ((pack.price / 100) / pack.credits).toFixed(3);
                const packName = String(pack?.name || '');

                return (
                  <motion.div
                    key={pack.id}
                    whileHover={{ y: -2, scale: 1.01 }}
                    transition={{ duration: 0.2 }}
                    className={`relative rounded-2xl border bg-white/50 dark:bg-zinc-900/50 backdrop-blur-xl p-4 ${
                      pack.popular
                        ? 'border-[--accent]/50 ring-1 ring-[--accent]/40'
                        : 'border-white/20 dark:border-white/10'
                    }`}
                    style={{ '--accent': ACCENT }}
                  >
                    {pack.popular ? (
                      <div className="absolute -top-3 left-4">
                        <span className="px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm" style={{ background: ACCENT }}>
                          {t('admin.credits_recommended')}
                        </span>
                      </div>
                    ) : null}

                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100">{packName}</h3>
                        <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">{t('admin.credits_one_time_pack')}</p>
                      </div>
                      {/* Pack badge: match admin topbar (dot + number), bigger, pulsing indicator, no coin icon */}
                      <div
                        className="h-12 md:h-14 px-3 md:px-4 rounded-xl flex items-center justify-center border border-white/20 dark:border-white/10"
                        style={{ background: `${ACCENT}25` }}
                        aria-label={`${pack.credits} credits`}
                      >
                        <div className="admin-credits-inner">
                          <span
                            className="admin-credits-dot hc-credits-dot-pulse"
                            aria-hidden="true"
                            style={{ width: 12, height: 12 }}
                          />
                          <span className="admin-credits-value" style={{ fontSize: 18, lineHeight: 1 }}>
                            {pack.credits}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-end justify-between mb-4">
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-extrabold text-zinc-900 dark:text-zinc-100">{pack.credits}</span>
                          <span className="text-sm font-semibold text-zinc-600 dark:text-zinc-400">credits</span>
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-500 mt-1">${perCredit} / credit</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-extrabold text-zinc-900 dark:text-zinc-100">${priceDollars}</div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-500">USD</div>
                      </div>
                    </div>

                    <button
                      onClick={() => handlePurchaseCredits(pack.id)}
                      disabled={purchaseLoading === pack.id}
                      className={`w-full py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${
                        purchaseLoading === pack.id
                          ? 'bg-zinc-300 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 cursor-not-allowed'
                          : 'text-white shadow-lg hover:shadow-xl'
                      }`}
                      style={purchaseLoading !== pack.id ? { 
                        background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)`,
                        boxShadow: `0 4px 16px ${ACCENT}40`
                      } : undefined}
                      type="button"
                    >
                      {purchaseLoading === pack.id ? 'Redirecting…' : 'Buy credits'}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Unsubscribe Modal */}
      <UnsubscribeModal
        isOpen={showUnsubscribeModal}
        onClose={() => setShowUnsubscribeModal(false)}
        onConfirm={handleUnsubscribe}
        loading={unsubscribeLoading}
      />
    </div>
  );
};

export default Credits;

